import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useDashboardFilters, formatPeriodForTimeRange, type DashboardScope } from '../contexts/DashboardFiltersContext';
import TimeRangeSelector, { CalendarIcon } from './TimeRangeSelector';
import DashboardSwitcher from './DashboardSwitcher';
import { cn } from '../../../lib/utils';

const UserIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const TeamIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const RefreshIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const CustomizeIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
  </svg>
);

const DoneIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const AddWidgetIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

export interface DashboardFilterBarProps {
  currentLayoutName?: string;
  onSaveAsDashboard?: () => void;
  onManageDashboards?: () => void;
  onResetLayout?: () => void;
  onRefresh?: () => void;
  isCustomizing?: boolean;
  onCustomizeToggle?: () => void;
  onOpenSettings?: () => void;
}

const DashboardFilterBar: React.FC<DashboardFilterBarProps> = ({
  currentLayoutName,
  onSaveAsDashboard,
  onManageDashboards,
  onResetLayout,
  onRefresh,
  isCustomizing = false,
  onCustomizeToggle,
  onOpenSettings,
}) => {
  const { colors } = useTheme();
  const { timeRange, setTimeRange, scope, setScope } = useDashboardFilters();

  const actionBtnBase =
    'p-2 rounded-lg transition-colors flex items-center justify-center min-h-[40px] min-w-[40px] sm:min-h-[36px] sm:min-w-[36px] touch-manipulation';

  return (
    <header
      className={cn(
        'flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-4 sm:gap-0 px-4 py-3.5 sm:px-6 sm:py-3 border-b shadow-sm',
        colors.surface,
        colors.border,
      )}
    >
      {onSaveAsDashboard != null && (
        <div className="flex items-center shrink-0 sm:mr-6">
          <DashboardSwitcher
            currentLayoutName={currentLayoutName}
            onSaveAsClick={onSaveAsDashboard}
            onManageClick={onManageDashboards}
            onResetLayoutClick={onResetLayout}
          />
        </div>
      )}

      <div
        className={cn(
          'flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 flex-1 min-w-0 rounded-xl border px-4 py-3 sm:py-2.5',
          colors.border,
          colors.surfaceHover,
        )}
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 min-w-0 flex-1">
          <div className={cn('flex items-center gap-2 shrink-0 text-xs font-semibold uppercase tracking-wider', colors.textMuted)}>
            <CalendarIcon className="w-3.5 h-3.5" />
            <span>Time</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
            <div className="overflow-x-auto -mx-1 px-1 sm:mx-0 sm:px-0 touch-pan-x min-w-0">
              <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
            </div>
            <span className={cn('text-sm tabular-nums', colors.textMuted)}>
              {timeRange === 'all' ? '(No Time Range)' : `(Date Period: ${formatPeriodForTimeRange(timeRange)})`}
            </span>
          </div>
        </div>
        <div className={cn('hidden sm:block w-px h-6 self-center opacity-20', colors.text)} aria-hidden />
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 min-w-0">
          <span className={cn('text-xs font-semibold uppercase tracking-wider shrink-0', colors.textMuted)}>
            Scope
          </span>
          <div className={cn('inline-flex items-center rounded-lg border p-0.5 min-h-[40px] sm:min-h-0', colors.border, colors.surface)}>
            {(['myWork', 'team'] as DashboardScope[]).map((option) => {
              const isSelected = scope === option;
              const isMyWork = option === 'myWork';
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => setScope(option)}
                  className={cn(
                    'inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 min-h-[36px] sm:min-h-0 text-sm font-medium transition-all duration-150',
                    isSelected
                      ? cn(colors.primary, colors.primaryText, 'shadow-sm')
                      : cn(colors.textMuted, colors.interactiveHover),
                  )}
                >
                  {isMyWork ? <UserIcon className="w-4 h-4" /> : <TeamIcon className="w-4 h-4" />}
                  {isMyWork ? 'My Work' : 'Team'}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-0.5 sm:ml-4 shrink-0 self-center sm:self-auto">
        {onCustomizeToggle != null && (
          <button
            type="button"
            onClick={onCustomizeToggle}
            className={cn(
              'inline-flex items-center justify-center rounded-lg min-h-[40px] min-w-[40px] sm:min-h-[36px] sm:min-w-[36px] p-2 transition-all duration-150 touch-manipulation',
              isCustomizing
                ? cn(colors.primary, colors.primaryText, 'shadow-md')
                : cn(colors.textMuted, colors.interactiveHover),
            )}
            title={isCustomizing ? 'Done editing' : 'Customize dashboard'}
            aria-label={isCustomizing ? 'Done editing' : 'Customize dashboard'}
          >
            {isCustomizing ? <DoneIcon className="w-4 h-4 shrink-0" /> : <CustomizeIcon className="w-4 h-4 shrink-0" />}
          </button>
        )}
        {onOpenSettings != null && (
          <button
            type="button"
            onClick={onOpenSettings}
            className={cn(actionBtnBase, colors.textMuted, colors.interactiveHover)}
            title="Add widgets"
            aria-label="Add widgets"
          >
            <AddWidgetIcon className="w-4 h-4 shrink-0" />
          </button>
        )}
        {onRefresh != null && (
          <button
            type="button"
            onClick={onRefresh}
            className={cn(actionBtnBase, colors.textMuted, colors.interactiveHover)}
            title="Refresh dashboard"
            aria-label="Refresh dashboard"
          >
            <RefreshIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    </header>
  );
};

export default DashboardFilterBar;
